import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  AccessibilityNew as AccessibilityNewIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  Bookmark as BookmarkIcon,
  AccessTimeFilled as AccessTimeFilledIcon,
  AccessAlarm as AccessAlarmIcon,
  TaskAlt as TaskAltIcon,
  PhonelinkErase as PhonelinkEraseIcon,
  ThumbUp as ThumbUpIcon,
  Cancel as CancelIcon,
  WorkHistory as WorkHistoryIcon,
  Backspace as BackspaceIcon,
  ShowChart as ShowChartIcon,
  BarChart as BarChartIcon,
  PendingActions as PendingActionsIcon,
  People as PeopleIcon,
  ArrowBack as ArrowBackIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, PointElement, LineElement, Title, Filler,
} from 'chart.js';

import { GetteamleaderdashboarddatabyteamleaderemailadvancedCRMDATA } from '../../Redux/Actions/RoleData_Actions';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

/* ─── Design tokens — IDENTICAL to TeamLeaderDashboard ──────────────────── */
const C = {
  bg:         '#F7F8FC',
  white:      '#FFFFFF',
  border:     '#E8EAF0',
  accent:     '#0D47A1',
  accentDark: '#0a3880',
  accentSoft: 'rgba(13,71,161,0.07)',
  text:       '#1C2340',
  textMuted:  '#7B829A',
  warning:    '#F79009',
  shadow:     '0 1px 3px rgba(16,24,40,0.08)',
  shadowMd:   '0 4px 16px rgba(16,24,40,0.10)',
};

const SIDEBAR_COLLAPSED = 64;

/* ─── Stage icons ─────────────────────────────────────────────────────────── */
const STAGE_ICONS = {
  Fresh:                    <TipsAndUpdatesIcon />,
  'No Stage':               <TipsAndUpdatesIcon />,
  'Team Leader Fresh':      <TipsAndUpdatesIcon />,
  'Team Leader Pending':    <PendingActionsIcon />,
  Pending:                  <PendingActionsIcon />,
  Follow:                   <BookmarkIcon />,
  'Follow Up':              <BookmarkIcon />,
  'Long Follow':            <BookmarkIcon />,
  Transfer:                 <BackspaceIcon />,
  Interested:               <ThumbUpIcon />,
  'Not Interested':         <BackspaceIcon />,
  Meeting:                  <AccessTimeFilledIcon />,
  'Following After Meeting':<WorkHistoryIcon />,
  'Follow After Meeting':   <WorkHistoryIcon />,
  'Cancel Meeting':         <CancelIcon />,
  'Done Deal':              <TaskAltIcon />,
  'No Answer':              <PhonelinkEraseIcon />,
  'No Answer 1st Call':     <AccessAlarmIcon />,
  Cancellation:             <CancelIcon />,
};

/* ─── stage colour — Pending = amber, rest = purple ─────────────────────── */
const getStageColor = (name) => {
  if (name === 'Team Leader Pending' || name === 'Pending') return C.warning;
  return C.accent;
};

/* ─── Stat card — identical shape to TeamLeaderDashboard ────────────────── */
const StatCard = ({ label, value, highlight, color, onClick }) => {
  const bg   = highlight ? (color || C.accent) : C.white;
  const bdr  = highlight ? (color || C.accent) : C.border;
  const shad = highlight ? `0 4px 16px ${color || C.accent}40` : C.shadow;
  return (
    <div
      onClick={onClick}
      style={{
        background: bg, border: `1px solid ${bdr}`, borderRadius: 12,
        padding: '20px 24px', minWidth: 160, height: 120,
        boxShadow: shad,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        cursor: onClick ? 'pointer' : 'default', flexShrink: 0,
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${color || C.accent}30`; } }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = shad; }}
    >
      <span style={{ fontSize: 11, fontWeight: 500, color: highlight ? 'rgba(255,255,255,0.75)' : C.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif" }}>{label}</span>
      <span style={{ fontSize: 26, fontWeight: 700, color: highlight ? '#fff' : C.text, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>{value ?? '—'}</span>
      {highlight && <div style={{ height: 3, width: 40, background: '#fff', borderRadius: 2, opacity: 0.4 }} />}
    </div>
  );
};

/* ─── Stage card — identical to TeamLeaderDashboard's StageCard ─────────── */
const StageCard = ({ stage, totalLeads, onStageClick }) => {
  const color  = getStageColor(stage.stageName);
  const count  = stage.leadsCount || 0;
  const pct    = totalLeads > 0 ? ((count / totalLeads) * 100).toFixed(1) : 0;
  const icon   = STAGE_ICONS[stage.stageName] || <AccessibilityNewIcon />;
  const isWarm = color === C.warning;

  return (
    <div
      onClick={() => onStageClick(stage)}
      style={{
        background: isWarm ? C.warning : C.white,
        border: `1px solid ${isWarm ? C.warning : C.border}`,
        borderRadius: 10, padding: '12px 16px', cursor: 'pointer',
        transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
        boxShadow: isWarm ? `0 4px 16px rgba(247,144,9,0.30)` : C.shadow,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, minHeight: 70,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 24px ${color}40`;
        if (!isWarm) e.currentTarget.style.borderColor = color;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = isWarm ? `0 4px 16px rgba(247,144,9,0.30)` : C.shadow;
        e.currentTarget.style.borderColor = isWarm ? C.warning : C.border;
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: isWarm ? 'rgba(255,255,255,0.85)' : C.textMuted, fontFamily: "'DM Sans', sans-serif", marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {stage.stageName}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <span style={{ fontSize: 24, fontWeight: 800, lineHeight: 1, color: isWarm ? '#fff' : color, fontFamily: "'DM Sans', sans-serif" }}>{count}</span>
          <span style={{ fontSize: 11, fontWeight: 500, color: isWarm ? 'rgba(255,255,255,0.7)' : C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{pct}%</span>
        </div>
      </div>
      <div style={{ flexShrink: 0, opacity: isWarm ? 0.9 : 0.55, display: 'flex', alignItems: 'center' }}>
        {React.cloneElement(icon, { sx: { color: isWarm ? '#fff' : color, fontSize: 22 } })}
      </div>
    </div>
  );
};

/* ─── Stage list row — identical to TeamLeaderDashboard's StageRow ──────── */
const StageRow = ({ stage, totalLeads, onStageClick }) => {
  const color = getStageColor(stage.stageName);
  const count = stage.leadsCount || 0;
  const pct   = totalLeads > 0 ? ((count / totalLeads) * 100).toFixed(1) : 0;
  const icon  = STAGE_ICONS[stage.stageName] || <AccessibilityNewIcon />;

  return (
    <div
      onClick={() => onStageClick(stage)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px', background: C.white,
        border: `1px solid ${C.border}`, borderRadius: 10,
        cursor: 'pointer', marginBottom: 8,
        transition: 'transform 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(4px)'; e.currentTarget.style.borderColor = color; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.borderColor = C.border; }}
    >
      <div style={{ width: 34, height: 34, borderRadius: 8, background: color + '12', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 18 } })}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: "'DM Sans', sans-serif", marginBottom: 5 }}>{stage.stageName}</div>
        <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color, fontFamily: "'DM Sans', sans-serif" }}>{count}</div>
        <div style={{ fontSize: 11, color: C.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{pct}%</div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════════════════════ */
export const TeamLeaderQuickNumbersCRMData = () => {
  const theme    = useTheme();
  const mode     = theme.palette.mode;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sidebarW = useSidebarWidth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [userlogin, setUserlogin] = useState(null);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  /* ── redux data ── */
  const dashboardData     = useSelector(s => s.AllRoleData.teamleaderdashboardcrmdata);
  const dashboard         = dashboardData?.data?.dashboard         || [];
  const teamLeaderFresh   = dashboardData?.data?.teamLeaderFresh   || { leadsCount: 0 };
  const teamLeaderPending = dashboardData?.data?.teamLeaderPending || { leadsCount: 0, salesIds: [] };
  const summary           = dashboardData?.data?.summary           || { totalLeads: 0 };
  const teamleaderInfo    = dashboardData?.data?.teamleaderInfo    || {};

  useEffect(() => {
    if (userlogin?.data?.email) {
      setLoading(true);
      dispatch(GetteamleaderdashboarddatabyteamleaderemailadvancedCRMDATA(userlogin.data.email))
        .finally(() => setLoading(false));
    }
  }, [userlogin, dispatch]);

  useEffect(() => { if (dashboardData) setLoading(false); }, [dashboardData]);

  /* ── sorted stages — same order as TeamLeaderDashboard ── */
  const STAGE_ORDER = ['Team Leader Pending','Team Leader Fresh','No Stage','Pending','Interested','Follow Up','Meeting','Follow After Meeting','Done Deal','Cancel Meeting','No Answer','Not Interested','Transfer','Long Follow','Follow','Following After Meeting','No Answer 1st Call','Cancellation'];

  const allStages = useMemo(() => {
    const regular = dashboard
      .filter(s => s.stageName !== 'Fresh' && s.stageName !== 'Team Leader Fresh' && s.stageName !== 'Team Leader Pending')
      .sort((a, b) => {
        const ia = STAGE_ORDER.indexOf(a.stageName), ib = STAGE_ORDER.indexOf(b.stageName);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1; if (ib !== -1) return 1; return 0;
      });
    return [
      { stageId: 'tl-pending', stageName: 'Team Leader Pending', leadsCount: teamLeaderPending.leadsCount, isTeamLeaderPending: true, salesIds: teamLeaderPending.salesIds || [] },
      { stageId: 'tl-fresh',   stageName: 'Team Leader Fresh',   leadsCount: teamLeaderFresh.leadsCount,   isTeamLeaderFresh: true },
      ...regular,
    ];
  }, [dashboard, teamLeaderFresh, teamLeaderPending]);

  const totalLeads = summary.totalLeads || 0;
  const doneDeals  = allStages.find(s => s.stageName === 'Done Deal')?.leadsCount || 0;
  const convRate   = totalLeads > 0 ? ((doneDeals / totalLeads) * 100).toFixed(1) : 0;

  /* ── stage click — same logic as TeamLeaderDashboard but routes to CRM pages ── */
  const handleStageClick = (stage) => {
    const keys = ['teamLeaderFreshMode','teamLeaderPendingMode','teamLeaderPendingSalesId','teamleaderfresh','teamleaderId','filterForSales','teamleaderpending','stagestorage','FilteredLeadsForNoStage','FilteredLeadsForTeamLeaderPending','freshFilterType','crmDataMode'];
    keys.forEach(k => localStorage.removeItem(k));
    localStorage.setItem('crmDataMode', 'true');

    const { stageName, isTeamLeaderFresh, isTeamLeaderPending, salesIds } = stage;
    const crmParams = { data: 'true', transferefromdata: 'false' };

    if (isTeamLeaderFresh) {
      const tlId = salesIds?.[0] || teamleaderInfo._id || userlogin?.data?._id || '';
      const q = new URLSearchParams({ stage: 'No Stage', sales: tlId, teamleaderfresh: 'true', freshmode: 'teamleader', ...crmParams });
      localStorage.setItem('stagestorage', 'No Stage');
      localStorage.setItem('filterForSales', tlId);
      localStorage.setItem('teamleaderId', tlId);
      localStorage.setItem('teamleaderfresh', 'true');
      localStorage.setItem('teamLeaderFreshMode', 'true');
      localStorage.setItem('freshFilterType', 'teamleader');
      navigate(`/TeamLeaderCRMDATAALLLeads?${q.toString()}`);

    } else if (isTeamLeaderPending) {
      const tlId = salesIds?.[0] || teamleaderInfo._id || userlogin?.data?._id || '';
      const q = new URLSearchParams({ stage: 'Pending', sales: tlId, teamleaderpending: 'true', pendingmode: 'teamleader', ...crmParams });
      localStorage.setItem('stagestorage', 'Pending');
      localStorage.setItem('teamleaderpending', 'true');
      localStorage.setItem('filterForSales', tlId);
      localStorage.setItem('teamLeaderPendingMode', 'true');
      localStorage.setItem('teamLeaderPendingSalesId', tlId);
      navigate(`/TeamLeaderCRMDATAALLLeads?${q.toString()}`);

    } else if (stageName === 'All Leads') {
      localStorage.setItem('stagestorage', '');
      navigate(`/TeamLeaderCRMDATAALLLeads?${new URLSearchParams(crmParams).toString()}`);

    } else {
      localStorage.setItem('stagestorage', stageName);
      const q = new URLSearchParams({ stage: encodeURIComponent(stageName), ...crmParams });
      navigate(`/TeamLeaderCRMDATAALLLeads?${q.toString()}`);
    }
  };

  /* ── chart ── */
  const chartData = useMemo(() => {
    const labels = allStages.map(s => s.stageName);
    const data   = allStages.map(s => s.leadsCount || 0);
    const colors = allStages.map(s => getStageColor(s.stageName));
    return {
      labels,
      datasets: [{ data, backgroundColor: colors, borderColor: mode === 'dark' ? '#1a1a2e' : '#fff', borderWidth: 2, hoverOffset: 12 }],
    };
  }, [allStages, mode]);

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { color: mode === 'dark' ? '#fff' : C.text, font: { size: 11, family: "'DM Sans', sans-serif" }, padding: 16, usePointStyle: true, pointStyle: 'circle' } },
      tooltip: {
        backgroundColor: C.white, titleColor: C.text, bodyColor: C.textMuted, borderColor: C.border, borderWidth: 1,
        callbacks: { label: ctx => { const t = ctx.dataset.data.reduce((a,b)=>a+b,0); return `${ctx.label}: ${ctx.raw} leads (${t>0?((ctx.raw/t)*100).toFixed(1):0}%)`; } },
      },
    },
    cutout: '62%',
  };

  /* ── loading ── */
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background: C.bg }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:48, height:48, borderRadius:'50%', border:`3px solid ${C.accentSoft}`, borderTop:`3px solid ${C.accent}`, animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }} />
        <div style={{ fontSize:14, color:C.textMuted, fontFamily:"'DM Sans', sans-serif" }}>Loading CRM Dashboard...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  /* ── render ── */
  return (
    <div style={{
      marginLeft: isMobile ? SIDEBAR_COLLAPSED : sidebarW,
      marginTop: 64,
      minHeight: 'calc(100vh - 64px)',
      background: C.bg,
      transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ padding: isMobile ? '16px' : '28px 28px 48px' }}>

        {/* ── Header ── */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:16, marginBottom:28 }}>
          <div>
            {/* back button */}
            <button
              onClick={() => navigate('/TeamLeaderDashboard')}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.textMuted, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans', sans-serif", marginBottom:12, transition:'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}
            >
              <ArrowBackIcon sx={{ fontSize:14 }} />
              Back to Dashboard
            </button>

            <div style={{ fontSize: isMobile ? 28 : 36, fontWeight:800, color:C.text, lineHeight:1.15, letterSpacing:'-0.02em', fontFamily:"'DM Sans', sans-serif" }}>
              CRM Data<br />Dashboard
            </div>
            <div style={{ fontSize:13.5, color:C.textMuted, marginTop:8, fontFamily:"'DM Sans', sans-serif" }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 10px', borderRadius:20, background: C.accentSoft, color: C.accent, fontSize:11, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', marginRight:8, fontFamily:"'DM Sans', sans-serif" }}>CRM Data</span>
              {teamleaderInfo.name || userlogin?.data?.name || 'Team Leader'} — data center pipeline.
              {dashboardData?.data?.meta && (
                <span style={{ marginLeft:8, fontSize:11, color:C.textMuted }}>
                  · {dashboardData.data.meta.executionTime} {dashboardData.data.meta.fromCache ? '(cached)' : '(fresh)'}
                </span>
              )}
            </div>
          </div>

          {/* Stat cards */}
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'stretch' }}>
            <StatCard label="All CRM Leads"  value={totalLeads}                   onClick={() => handleStageClick({ stageName:'All Leads' })} />
            <StatCard label="CRM Pending"    value={teamLeaderPending.leadsCount} color={C.warning} highlight onClick={() => handleStageClick({ stageName:'Team Leader Pending', isTeamLeaderPending:true, salesIds:teamLeaderPending.salesIds })} />
            <StatCard label="CRM Fresh"      value={teamLeaderFresh.leadsCount}   color={C.accent}  highlight onClick={() => handleStageClick({ stageName:'Team Leader Fresh', isTeamLeaderFresh:true })} />
            <StatCard label="Conv. Rate"     value={`${convRate}%`} highlight />
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <ShowChartIcon sx={{ color:C.accent, fontSize:18 }} />
            <span style={{ fontSize:16, fontWeight:700, color:C.text, fontFamily:"'DM Sans', sans-serif" }}>CRM Data Stages</span>
            <span style={{ fontSize:12, color:C.textMuted, fontFamily:"'DM Sans', sans-serif" }}>({allStages.length}) — click any card to view leads</span>
          </div>
        </div>

        {/* ── Stage cards grid — same layout as TeamLeaderDashboard ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:12, marginBottom:28 }}>
          {allStages.map(stage => (
            <StageCard
              key={stage.stageId || stage.stageName}
              stage={stage}
              totalLeads={totalLeads}
              onStageClick={handleStageClick}
            />
          ))}
        </div>

        {/* ── Chart + List row — same as TeamLeaderDashboard ── */}
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:16, marginBottom:28 }}>

          {/* Doughnut */}
          <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:C.shadow, overflow:'hidden' }}>
            <div style={{ padding:'20px 22px 16px', borderBottom:`1px solid ${C.border}` }}>
              <div style={{ fontSize:15, fontWeight:700, color:C.text, fontFamily:"'DM Sans', sans-serif" }}>
                <BarChartIcon sx={{ fontSize:17, color:C.accent, verticalAlign:'middle', mr:0.5 }} />
                CRM Distribution
              </div>
              <div style={{ fontSize:12, color:C.textMuted, fontFamily:"'DM Sans', sans-serif", marginTop:3 }}>Visual breakdown across all CRM stages</div>
            </div>
            <div style={{ padding:'20px 22px', height:320 }}>
              <Doughnut data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Stages list */}
          <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:C.shadow, overflow:'hidden' }}>
            <div style={{ padding:'20px 22px 16px', borderBottom:`1px solid ${C.border}` }}>
              <div style={{ fontSize:15, fontWeight:700, color:C.text, fontFamily:"'DM Sans', sans-serif" }}>
                <BookmarkIcon sx={{ fontSize:17, color:C.accent, verticalAlign:'middle', mr:0.5 }} />
                CRM Stages Overview
              </div>
              <div style={{ fontSize:12, color:C.textMuted, fontFamily:"'DM Sans', sans-serif", marginTop:3 }}>Click any stage to view CRM leads</div>
            </div>
            <div style={{ padding:'16px 22px', maxHeight:320, overflowY:'auto' }}>
              {allStages.map(stage => (
                <StageRow
                  key={stage.stageId || stage.stageName}
                  stage={stage}
                  totalLeads={totalLeads}
                  onStageClick={handleStageClick}
                />
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
